import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { ShoppingCart, Minus, Plus, Trash2, MessageCircle, Mail } from "lucide-react";
import { useCartStore } from "@/stores/cartStore";
import { formatPrice } from "@/lib/shopify";

// Store contact — used to route orders when Shopify is not in use.
const WHATSAPP_NUMBER = "923707851120"; // international format, no "+"
const ORDER_EMAIL = "malikmuzammilfive@gmail.com";

export const CartDrawer = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { items, updateQuantity, removeItem, clearCart } = useCartStore();
  const totalItems = items.reduce((sum, i) => sum + i.quantity, 0);
  const totalPrice = items.reduce(
    (sum, i) => sum + parseFloat(i.price.amount) * i.quantity,
    0,
  );
  const currency = items[0]?.price.currencyCode || "PKR";

  const buildOrderText = () => {
    const lines = items.map(
      (i, idx) =>
        `${idx + 1}. ${i.product.node.title}${
          i.variantTitle && i.variantTitle !== "Default" ? ` (${i.variantTitle})` : ""
        } × ${i.quantity} — ${formatPrice(
          parseFloat(i.price.amount) * i.quantity,
          i.price.currencyCode,
        )}`,
    );
    return [
      "Hello Trendora Marts, I'd like to place an order:",
      "",
      ...lines,
      "",
      `Total: ${formatPrice(totalPrice, currency)}`,
      "",
      "Name: ",
      "Phone: ",
      "Delivery Address: ",
      "Payment: Cash on Delivery",
    ].join("\n");
  };

  const handleWhatsApp = () => {
    if (items.length === 0) return;
    const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
      buildOrderText(),
    )}`;
    window.open(url, "_blank");
    setIsOpen(false);
  };

  const handleEmail = () => {
    if (items.length === 0) return;
    const subject = `New Order — ${totalItems} item${totalItems !== 1 ? "s" : ""}`;
    const url = `mailto:${ORDER_EMAIL}?subject=${encodeURIComponent(
      subject,
    )}&body=${encodeURIComponent(buildOrderText())}`;
    window.location.href = url;
    setIsOpen(false);
  };

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative text-foreground hover:text-[--teal]"
          aria-label="Cart"
        >
          <ShoppingCart className="h-5 w-5" />
          {totalItems > 0 && (
            <Badge className="absolute -top-1 -right-1 h-5 min-w-5 rounded-full p-0 flex items-center justify-center text-[10px] bg-[--gold] text-[--navy] border-0">
              {totalItems}
            </Badge>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent className="w-full sm:max-w-lg flex flex-col h-full">
        <SheetHeader className="flex-shrink-0">
          <SheetTitle className="font-serif text-2xl">Your Cart</SheetTitle>
          <SheetDescription>
            {totalItems === 0
              ? "Your cart is empty"
              : `${totalItems} item${totalItems !== 1 ? "s" : ""}`}
          </SheetDescription>
        </SheetHeader>
        <div className="flex flex-col flex-1 pt-6 min-h-0">
          {items.length === 0 ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <ShoppingCart className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">
                  Start shopping our premium collection
                </p>
              </div>
            </div>
          ) : (
            <>
              <div className="flex-1 overflow-y-auto pr-2 min-h-0">
                <div className="space-y-4">
                  {items.map((item) => (
                    <div
                      key={item.variantId}
                      className="flex gap-4 p-2 border-b border-border/40"
                    >
                      <div className="w-20 h-20 bg-secondary rounded-md overflow-hidden flex-shrink-0">
                        {item.product.node.images?.edges?.[0]?.node && (
                          <img
                            src={item.product.node.images.edges[0].node.url}
                            alt={item.product.node.title}
                            className="w-full h-full object-cover"
                          />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium truncate">
                          {item.product.node.title}
                        </h4>
                        {item.variantTitle && item.variantTitle !== "Default" && (
                          <p className="text-xs text-muted-foreground">
                            {item.variantTitle}
                          </p>
                        )}
                        <p className="font-semibold text-[--gold] mt-1">
                          {formatPrice(item.price.amount, item.price.currencyCode)}
                        </p>
                      </div>
                      <div className="flex flex-col items-end gap-2 flex-shrink-0">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={() => removeItem(item.variantId)}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() =>
                              updateQuantity(item.variantId, item.quantity - 1)
                            }
                          >
                            <Minus className="h-3 w-3" />
                          </Button>
                          <span className="w-7 text-center text-sm">
                            {item.quantity}
                          </span>
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() =>
                              updateQuantity(item.variantId, item.quantity + 1)
                            }
                          >
                            <Plus className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex-shrink-0 space-y-3 pt-4 border-t bg-background">
                <div className="flex justify-between items-center">
                  <span className="text-lg">Total</span>
                  <span className="text-2xl font-serif font-bold text-[--gold]">
                    {formatPrice(totalPrice, currency)}
                  </span>
                </div>
                <Button
                  onClick={handleWhatsApp}
                  className="w-full bg-[#25D366] hover:bg-[#1ebe5a] text-white"
                  size="lg"
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Order on WhatsApp
                </Button>
                <Button
                  onClick={handleEmail}
                  variant="outline"
                  className="w-full border-[--navy] text-[--navy] hover:bg-[--navy] hover:text-white"
                  size="lg"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Order by Email
                </Button>
                <div className="flex items-center justify-between text-xs text-muted-foreground pt-1">
                  <span>Cash on Delivery available</span>
                  <button
                    onClick={clearCart}
                    className="hover:text-[--navy] underline underline-offset-2"
                  >
                    Clear cart
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};
